import { useStore } from "@mapcraft-tasks/client-store/react";
import { UNAVAILABLE } from "@mapcraft-tasks/domain";
import { Fragment, type ReactNode, useMemo } from "react";

/**
 * Заголовок вне режима редактирования: markdown отрендерен, меншены — заголовками.
 *
 * Сырой `[[id]]` человек не видит НИКОГДА — ни здесь, ни в редакторе (ui-spec 8.4).
 * Инлайн-markdown, наоборот, показывается сырым в фокусе и отрендеренным вне его;
 * ширина строки при этом меняется — принято как данность.
 */
const MENTION_RE = /\[\[([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\]\]/gi;
const INLINE_RE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|~~[^~]+~~)/g;

export function RenderedTitle({ text, onOpenMention }: { text: string; onOpenMention?: (id: string) => void }) {
	const store = useStore();

	return useMemo(() => {
		const nodes = store.getSnapshot().replica.nodes;
		const parts: ReactNode[] = [];
		let cursor = 0;
		let key = 0;

		for (const match of text.matchAll(MENTION_RE)) {
			const at = match.index;
			parts.push(<Fragment key={key++}>{inline(text.slice(cursor, at))}</Fragment>);
			const id = match[1]!.toLowerCase();
			// Цели нет в реплике (архив не реплицируется, либо нода ещё не приехала) —
			// нейтральная заглушка. uuid не показываем; ссылка при этом живая.
			const title = nodes.get(id)?.title ?? UNAVAILABLE;
			parts.push(
				<button
					key={key++}
					type="button"
					className="mention"
					onClick={(e) => {
						e.stopPropagation();
						onOpenMention?.(id);
					}}
				>
					{title}
				</button>
			);
			cursor = at + match[0].length;
		}
		parts.push(<Fragment key={key++}>{inline(text.slice(cursor))}</Fragment>);
		return <>{parts}</>;
	}, [text, store, onOpenMention]);
}

function inline(chunk: string): ReactNode[] {
	const out: ReactNode[] = [];
	let cursor = 0;
	let key = 0;
	for (const match of chunk.matchAll(INLINE_RE)) {
		const at = match.index;
		out.push(<Fragment key={key++}>{chunk.slice(cursor, at)}</Fragment>);
		const token = match[0];
		if (token.startsWith("**")) {
			out.push(
				<strong key={key++} className="font-bold text-ink-strong">
					{token.slice(2, -2)}
				</strong>
			);
		} else if (token.startsWith("~~")) {
			out.push(
				<s key={key++} className="text-ink-muted">
					{token.slice(2, -2)}
				</s>
			);
		} else if (token.startsWith("`")) {
			out.push(
				<code key={key++} className="rounded-sm bg-panel-dim px-1 font-mono text-xs text-ink-muted">
					{token.slice(1, -1)}
				</code>
			);
		} else {
			out.push(
				<em key={key++} className="italic">
					{token.slice(1, -1)}
				</em>
			);
		}
		cursor = at + token.length;
	}
	out.push(<Fragment key={key++}>{chunk.slice(cursor)}</Fragment>);
	return out;
}
